import React, { useCallback, useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import classNames from 'classnames'

import { FormTemplateMappingFormValues, FormTemplateMappingSource, FormTemplateMappingTarget } from '../../../../models/FormTemplate'

import { MultiSelectDropdown } from '../../../../components/MultiSelectDropdown/MultiSelectDropdown'

import { doSourcesMatch, formTemplateMappingTargetToSelectOption } from '../utils'

interface FormTemplateMappingTargetColumnProps {
  targets: FormTemplateMappingTarget[]
  source: FormTemplateMappingSource
}

export const FormTemplateMappingTargetColumn: React.FC<FormTemplateMappingTargetColumnProps> = ({ targets, source }) => {
  const { setValue, watch } = useFormContext<FormTemplateMappingFormValues>()

  const mapping = watch('mapping')

  const targetIds = useMemo(
    () =>
      Object.entries(mapping)
        .filter(([, mappingSource]) => doSourcesMatch(source, mappingSource))
        .map(([targetId]) => targetId),
    [mapping, source],
  )

  const targetOptions = useMemo(
    () => targets.filter((target) => targetIds.includes(target.id) || !Object.keys(mapping).includes(target.id)).map(formTemplateMappingTargetToSelectOption),
    [mapping, targetIds, targets],
  )

  const handleSelectedChanged = useCallback(
    (selectedTargetIds: string[]) => {
      const mappingEntriesWithoutSelectedTargetIds = Object.entries(mapping).filter(([targetId]) => !targetIds.includes(targetId))
      const newMappingEntries = selectedTargetIds.map((targetId) => [targetId, source])
      setValue('mapping', Object.fromEntries([...mappingEntriesWithoutSelectedTargetIds, ...newMappingEntries]))
    },
    [mapping, setValue, source, targetIds],
  )

  const selectDropdownName =
    source.type === 'form_field' ? (source.option ? `form_field_${source.path}_${source.option}` : `form_field_${source.path}`) : `variable_${source.id}`

  return (
    <div className='w-50 d-flex flex-column px-3 zocks-py-0.75' style={{ maxWidth: '50%' }}>
      <MultiSelectDropdown
        name={selectDropdownName}
        initialSelectedOptions={targetIds}
        options={targetOptions}
        onSelectedChanged={handleSelectedChanged}
        TitleComponent={
          <span
            className={classNames('paragraph-medium overflow-hidden text-nowrap', {
              'zocks-text-gray-500': !targetIds.length,
            })}
          >
            {targetIds.length
              ? targetOptions
                  .filter((option) => targetIds.includes(option.id))
                  .map((option) => option.label)
                  .join(', ')
              : 'No match found - select field or skip'}
          </span>
        }
      />
    </div>
  )
}
